<template>
  <div class="teacher-typer-page">
    <shared-header />
    <div class="teacher-typing-assignments">
    <el-card class="main-card" shadow="hover">
      <template #header>
        <span class="card-title">打字练习 · 学生作业</span>
      </template>

      <div class="toolbar">
        <el-select
          v-model="remoteSelect.value"
          class="student-remote-select"
          filterable
          remote
          clearable
          reserve-keyword
          :remote-method="searchStudents"
          :loading="remoteSelect.loading"
          placeholder="远程搜索学生姓名"
          value-key="userId"
          @change="onRemoteStudentSelect"
          @keydown.stop
        >
          <el-option
            v-for="item in remoteSelect.options"
            :key="item.userId"
            :label="`${item.userName} : ${item.chineseName}`"
            :value="item"
          />
        </el-select>
      </div>

      <div v-if="boundStudent" class="bound-bar">
        <span class="bound-label">当前学生</span>
        <el-tag type="success" size="large">
          {{ boundStudent.chineseName || '—' }}
        </el-tag>
        <span class="hint-muted bound-hint">换人请清空上方下拉框或重新搜索选择。</span>
      </div>
      <div v-else class="hint-muted">请先搜索并选择一名学生；需要换人时重新搜索选择，或点击下拉框右侧清空。</div>

      <el-divider />

      <div v-if="boundStudent" class="filter-bar">
        <span class="filter-label">状态筛选</span>
        <el-radio-group v-model="statusFilter" size="small">
          <el-radio-button label="all">全部</el-radio-button>
          <el-radio-button label="pending">待完成</el-radio-button>
          <el-radio-button label="done">已完成</el-radio-button>
        </el-radio-group>
      </div>

      <div v-loading="listLoading" class="table-wrap">
        <el-table
          v-if="boundStudent"
          :data="filteredAssignments"
          stripe
          border
          :empty-text="assignmentsTableEmptyText"
          :row-class-name="assignmentRowClassName"
          style="width: 100%"
          @row-click="onRowClick"
        >
          <el-table-column type="expand" width="52">
            <template #default="{ row }">
              <div class="assignment-results-expand" @click.stop>
                <div class="result-title">提交记录</div>
                <p class="result-table-hint hint-muted">点击某一行的任意单元格可查看本次打字的原文、错词与录入对照。</p>
                <div v-if="!row.results.length" class="hint-muted">暂无提交记录</div>
                <el-table
                  v-else
                  :data="row.results"
                  size="small"
                  border
                  :row-class-name="resultRecordRowClassName"
                  @row-click="(resultRow) => openResultSubmissionDetail(row, resultRow)"
                >
                  <el-table-column prop="wpm" label="WPM" width="90" />
                  <el-table-column prop="typedCount" label="打词数" width="100" />
                  <el-table-column prop="timeUsed" label="用时(秒)" width="100" />
                  <el-table-column label="通过" width="90" align="center">
                    <template #default="{ row: result }">
                      <el-tag :type="resultPassTagType(result.passed)" size="small">
                        {{ result.passed ? '通过' : '未通过' }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="完成全文" width="100" align="center">
                    <template #default="{ row: result }">{{ result.finished ? '是' : '否' }}</template>
                  </el-table-column>
                  <el-table-column prop="createdAt" label="提交时间" min-width="160" />
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="title" label="作业标题" min-width="160" show-overflow-tooltip />
          <el-table-column label="状态" width="112" align="center">
            <template #default="{ row }">
              <el-tag :type="statusTagType(row.status)" effect="light" size="small" class="status-tag">
                {{ row.statusLabel }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="wpm" label="WPM 要求" width="100" />
          <el-table-column prop="standardRate" label="准确率%" width="100" />
          <el-table-column prop="countdown" label="计时(秒)" width="100" />
          <el-table-column prop="assignedAt" label="布置时间" width="155" />
          <el-table-column prop="submittedAt" label="提交时间" width="155">
            <template #default="{ row }">{{ row.submittedAt || '—' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" link @click.stop="openDetail(row)">详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>

    <el-dialog
      v-model="resultSubmissionVisible"
      title="本次打字提交详情"
      width="900px"
      destroy-on-close
      append-to-body
      class="result-submission-dialog"
    >
      <template v-if="resultSubmissionAssignment && resultSubmissionResult">
        <el-descriptions :column="2" border size="small" class="result-submission-meta">
          <el-descriptions-item label="WPM">{{ resultSubmissionResult.wpm }}</el-descriptions-item>
          <el-descriptions-item label="打词数">{{ resultSubmissionResult.typedCount }}</el-descriptions-item>
          <el-descriptions-item label="用时(秒)">{{ resultSubmissionResult.timeUsed }}</el-descriptions-item>
          <el-descriptions-item label="是否通过">
            <el-tag :type="resultPassTagType(resultSubmissionResult.passed)" size="small">
              {{ resultSubmissionResult.passed ? '通过' : '未通过' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="完成全文">{{ resultSubmissionResult.finished ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ resultSubmissionResult.createdAt }}</el-descriptions-item>
          <el-descriptions-item v-if="resultSubmissionWordAccuracy" label="准确率" :span="2">
            {{ resultSubmissionWordAccuracy.strictCorrect }} / {{ resultSubmissionWordAccuracy.total }}
           （{{ resultSubmissionWordAccuracy.strictPercent }}%）
            <span class="accuracy-desc">完全打对词数 ÷ 总词数</span>
          </el-descriptions-item>
          <el-descriptions-item v-if="resultSubmissionWordAccuracy" label="不计标点准确率" :span="2">
            {{ resultSubmissionWordAccuracy.strictCorrect + resultSubmissionWordAccuracy.punctuationOnlyWrong }} /
            {{ resultSubmissionWordAccuracy.total }}
            （{{ resultSubmissionWordAccuracy.relaxedPercent }}%）
            <span class="accuracy-desc">（完全打对 + 仅标点错误）÷ 总词数</span>
          </el-descriptions-item>
          <el-descriptions-item v-else label="准确率 / 不计标点准确率" :span="2">
            <span class="hint-muted">需有逐词录入快照（typedContent）方可按词统计</span>
          </el-descriptions-item>
        </el-descriptions>

        <div class="result-section-title">训练用文本（学生端对照的英文）</div>
        <pre class="result-section-pre">{{ resultSubmissionPracticeText || '—' }}</pre>

        <div class="result-section-title">学生当次录入（按词：蓝=正确，红=错误，灰=未录入）</div>
        <div
          v-if="resultSubmissionTypedHtml"
          class="result-typed-html"
          v-html="resultSubmissionTypedHtml"
        />
        <div v-else class="hint-muted">本次记录未保存逐词录入快照（旧数据可能仅有汇总字段）。</div>

        <div class="result-section-title">错词与拼写差异</div>
        <div v-if="resultSubmissionWrongRows.length">
          <el-table
            :data="resultSubmissionWrongRows"
            class="wrong-words-table"
            size="small"
            border
            stripe
            max-height="380"
            :row-key="(row) => 'ww-' + row.index"
          >
            <el-table-column type="index" label="序号" width="56" align="center" :index="(i) => i + 1" />
            <el-table-column prop="correct" label="原词" min-width="110" show-overflow-tooltip />
            <el-table-column prop="input" label="学生输入" min-width="110" show-overflow-tooltip />
            <el-table-column label="对比" min-width="220">
              <template #default="{ row }">
                <div class="wrong-words-table-diff">
                  <span
                    v-for="(token, tokenIdx) in row.tokens"
                    :key="'t-' + row.index + '-' + tokenIdx"
                    :class="['wrong-diff-char', 'wrong-diff-' + token.type]"
                  >
                    <span class="wrong-diff-main-char">{{ token.char }}</span>
                    <span v-if="token.type === 'replace'" class="wrong-diff-expected">{{ token.expected }}</span>
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="仅标点错误" width="104" align="center">
              <template #default="{ row }">{{ row.punctuationOnly ? '是' : '否' }}</template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else-if="resultSubmissionIncorrectFallback.length" class="hint-muted">
          仅有错词原文列表（无逐词录入时无法还原「怎么错的」）：
          <span>{{ resultSubmissionIncorrectFallback.join('、') }}</span>
        </div>
        <div v-else class="hint-muted">无错词记录，或本次未保存详细数据。</div>
      </template>
    </el-dialog>

    <el-dialog v-model="detailVisible" title="作业详情" width="640px" destroy-on-close>
      <template v-if="detailRow">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="标题">{{ detailRow.title }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="statusTagType(detailRow.status)" effect="light" size="small">
              {{ detailRow.statusLabel }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="WPM / 准确率标准 / 计时">
            {{ detailRow.wpm }} / {{ detailRow.standardRate }}% / {{ detailRow.countdown }} 秒
          </el-descriptions-item>
          <el-descriptions-item label="区分大小写">{{ detailRow.caseSensitive ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="布置 / 截止">{{ detailRow.assignedAt }} — {{ detailRow.dueAt }}</el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ detailRow.submittedAt || '—' }}</el-descriptions-item>
          <el-descriptions-item v-if="detailRow.bestWpm != null" label="最佳 WPM / 准确率">
            {{ detailRow.bestWpm }} / {{ detailRow.bestAccuracy }}%
          </el-descriptions-item>
          <el-descriptions-item v-if="detailRow.teacherComment" label="教师评语">
            {{ detailRow.teacherComment }}
          </el-descriptions-item>
          <el-descriptions-item label="原文">
            <pre class="detail-pre">{{ detailRow.originalText }}</pre>
          </el-descriptions-item>
          <el-descriptions-item label="AI 参考文本">
            <pre class="detail-pre">{{ detailRow.AIgeneratedText }}</pre>
          </el-descriptions-item>
        </el-descriptions>
      </template>
    </el-dialog>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import SharedHeader from '../../shared/SharedHeader.vue'
import {
  normalizeTyperAssignmentList,
  readBoundStudentFromStorage,
  unwrapApiData
} from '../../../utils/typerNormalize'

function normalizeTyperWord (str, caseSensitive) {
  if (str == null || typeof str !== 'string') return ''
  const t = str.trim()
  return caseSensitive ? t : t.toLowerCase()
}

/** 去掉首尾非字母数字字符（引号、逗号、句号等），用于判断「是否仅为标点/包裹符号差异」 */
function stripOuterNonAlphanumeric (str) {
  return String(str ?? '')
    .replace(/^[^a-zA-Z0-9]+/, '')
    .replace(/[^a-zA-Z0-9]+$/, '')
}

/**
 * 整词在大小写规则下不一致，但去掉首尾标点/符号后一致 → 视为仅标点类错误（不含词内拼写变化，如 its / it's）
 */
function isPunctuationOnlyDifference (correct, input, caseSensitive) {
  const c = String(correct ?? '').trim()
  const inp = String(input ?? '').trim()
  if (!inp) return false
  if (normalizeTyperWord(c, caseSensitive) === normalizeTyperWord(inp, caseSensitive)) {
    return false
  }
  const oc = stripOuterNonAlphanumeric(c)
  const oi = stripOuterNonAlphanumeric(inp)
  return normalizeTyperWord(oc, caseSensitive) === normalizeTyperWord(oi, caseSensitive)
}

/** 与 Typer Index.vue 中 buildWrongWordDiffTokens 一致，用于展示字符级差异 */
function buildTyperWrongWordDiffTokens (correctWord, inputWord, caseSensitive) {
  const expected = String(correctWord ?? '')
  const typed = String(inputWord ?? '')
  const eChars = [...expected]
  const tChars = [...typed]
  const m = eChars.length
  const n = tChars.length
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j
  const charEqual = (a, b) => {
    if (a === b) return true
    if (
      !caseSensitive &&
      /[a-zA-Z]/.test(a) &&
      /[a-zA-Z]/.test(b)
    ) {
      return a.toLowerCase() === b.toLowerCase()
    }
    return false
  }
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const same = charEqual(eChars[i - 1], tChars[j - 1])
      if (same) {
        dp[i][j] = dp[i - 1][j - 1]
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + 1
        )
      }
    }
  }
  const tokens = []
  let i = m
  let j = n
  while (i > 0 || j > 0) {
    if (
      i > 0 &&
      j > 0 &&
      charEqual(eChars[i - 1], tChars[j - 1]) &&
      dp[i][j] === dp[i - 1][j - 1]
    ) {
      tokens.push({ char: tChars[j - 1], type: 'correct' })
      i--
      j--
      continue
    }
    if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
      tokens.push({
        char: tChars[j - 1],
        expected: eChars[i - 1],
        type: 'replace'
      })
      i--
      j--
      continue
    }
    if (j > 0 && dp[i][j] === dp[i][j - 1] + 1) {
      tokens.push({ char: tChars[j - 1], type: 'extra' })
      j--
      continue
    }
    if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      tokens.push({ char: eChars[i - 1], type: 'missing' })
      i--
    }
  }
  return tokens.reverse()
}

const TEACHER_TYPING_BOUND_STUDENT_STORAGE_PREFIX = 'vocTyperTeacherAssignmentsBoundStudent'

export default {
  name: 'TeacherTypingStudentAssignments',
  components: { SharedHeader },
  data () {
    return {
      remoteSelect: {
        value: null,
        options: [],
        loading: false
      },
      boundStudent: null,
      assignments: [],
      /** all | pending | done（已完成 = 已提交 + 已批改） */
      statusFilter: 'all',
      listLoading: false,
      detailVisible: false,
      detailRow: null,
      resultSubmissionVisible: false,
      resultSubmissionAssignment: null,
      resultSubmissionResult: null
    }
  },
  computed: {
    filteredAssignments () {
      const list = this.assignments
      if (this.statusFilter === 'pending') {
        return list.filter((a) => a.status === 'pending')
      }
      if (this.statusFilter === 'done') {
        return list.filter((a) => a.status === 'submitted' || a.status === 'graded')
      }
      return list
    },

    assignmentsTableEmptyText () {
      if (!this.assignments.length) return '暂无作业'
      if (!this.filteredAssignments.length) return '当前筛选条件下暂无作业'
      return '暂无作业'
    },

    resultSubmissionPracticeText () {
      const a = this.resultSubmissionAssignment
      if (!a) return ''
      const ai = (a.AIgeneratedText || a.aIgeneratedText || '').trim()
      if (ai) return ai
      return (a.originalText || '').trim()
    },

    resultSubmissionTypedHtml () {
      const r = this.resultSubmissionResult
      if (!r) return ''
      const html = r.typedContent
      if (html == null || String(html).trim() === '') return ''
      return String(html)
    },

    resultSubmissionWrongRows () {
      if (!this.resultSubmissionAssignment || !this.resultSubmissionResult) return []
      return this.buildWrongWordRows(this.resultSubmissionAssignment, this.resultSubmissionResult)
    },

    resultSubmissionIncorrectFallback () {
      const r = this.resultSubmissionResult
      if (!r) return []
      const raw = r.incorrectWords
      if (raw == null || String(raw).trim() === '') return []
      return String(raw)
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean)
    },

    /** 按词：准确率 = 完全打对/总数；不计标点 = (完全打对+仅标点错)/总数 */
    resultSubmissionWordAccuracy () {
      const assignment = this.resultSubmissionAssignment
      const result = this.resultSubmissionResult
      if (!assignment || !result) return null
      const body = (
        (assignment.AIgeneratedText || assignment.aIgeneratedText || '').trim() ||
        (assignment.originalText || '').trim()
      )
      if (!body) return null
      const expectedWords = body.split(/\s+/).filter(Boolean)
      const total = expectedWords.length
      if (!total) return null
      const spans = this.parseTypedContentSpans(result.typedContent)
      if (!spans.length) return null
      const caseSensitive = !!assignment.caseSensitive
      let strictCorrect = 0
      let punctuationOnlyWrong = 0
      for (let i = 0; i < total; i++) {
        const correct = expectedWords[i] ?? ''
        const input = this.typerSpanToInput(spans[i])
        const fullMatch =
          !!correct &&
          !!input &&
          normalizeTyperWord(input, caseSensitive) === normalizeTyperWord(correct, caseSensitive)
        if (fullMatch) {
          strictCorrect++
        } else if (isPunctuationOnlyDifference(correct, input, caseSensitive)) {
          punctuationOnlyWrong++
        }
      }
      const strictRate = (strictCorrect / total) * 100
      const relaxedRate = ((strictCorrect + punctuationOnlyWrong) / total) * 100
      return {
        total,
        strictCorrect,
        punctuationOnlyWrong,
        strictPercent: strictRate.toFixed(2),
        relaxedPercent: relaxedRate.toFixed(2)
      }
    }
  },
  mounted () {
    this.syncBoundStudentFromStorage()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeUnmount () {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  watch: {
    '$route' (to) {
      if (to.path === '/vocTyper/student-assignments') {
        this.syncBoundStudentFromStorage()
      }
    }
  },
  methods: {
    boundStudentStorageKey () {
      const tid = (localStorage.getItem('userId') || '').trim() || '0'
      return `${TEACHER_TYPING_BOUND_STUDENT_STORAGE_PREFIX}_${tid}`
    },

    persistBoundStudent () {
      if (!this.boundStudent?.userId) {
        try {
          localStorage.removeItem(this.boundStudentStorageKey())
        } catch (e) {
          console.warn(e)
        }
        return
      }
      try {
        localStorage.setItem(
          this.boundStudentStorageKey(),
          JSON.stringify({
            userId: this.boundStudent.userId,
            userName: this.boundStudent.userName,
            chineseName: this.boundStudent.chineseName
          })
        )
      } catch (e) {
        console.warn(e)
      }
    },

    readPersistedBoundStudent () {
      try {
        const raw = localStorage.getItem(this.boundStudentStorageKey())
        if (!raw) return null
        const o = JSON.parse(raw)
        const uid = Number(o.userId)
        if (!Number.isFinite(uid) || uid <= 0) return null
        return {
          userId: uid,
          userName: o.userName != null ? String(o.userName) : '',
          chineseName: o.chineseName != null ? String(o.chineseName) : ''
        }
      } catch {
        return null
      }
    },

    /** Home / Header 绑定的学生优先于本页历史选择 */
    resolveBoundStudent () {
      return readBoundStudentFromStorage() || this.readPersistedBoundStudent()
    },

    handleVisibilityChange () {
      if (document.visibilityState !== 'visible') return
      if (this.$route.path !== '/vocTyper/student-assignments') return
      this.syncBoundStudentFromStorage()
    },

    syncBoundStudentFromStorage () {
      const s = this.resolveBoundStudent()
      if (!s) return
      if (this.boundStudent?.userId === s.userId) {
        this.boundStudent = {
          userId: s.userId,
          userName: s.userName,
          chineseName: s.chineseName
        }
        this.remoteSelect.value = { ...s, id: s.userId }
        this.loadAssignments()
        return
      }
      this.remoteSelect.value = { ...s, id: s.userId }
      this.remoteSelect.options = [{ ...s, id: s.userId }]
      this.bindStudentAndLoad(s, { silent: true })
    },

    clearPersistedBoundStudent () {
      try {
        localStorage.removeItem(this.boundStudentStorageKey())
      } catch (e) {
        console.warn(e)
      }
    },

    bindStudentAndLoad (student, { silent = false } = {}) {
      if (!student || !student.userId) return
      this.boundStudent = {
        userId: student.userId,
        userName: student.userName,
        chineseName: student.chineseName
      }
      this.persistBoundStudent()
      this.loadAssignments()
      if (!silent) {
        ElMessage.success('已加载该学生的作业')
      }
    },

    formatToMinute (value) {
      if (value == null || value === '') return '—'
      const s = String(value).trim()
      if (!s) return '—'
      // 统一到分钟显示：YYYY-MM-DD HH:mm
      const normalized = s.replace('T', ' ')
      if (normalized.length >= 16) return normalized.slice(0, 16)
      return normalized
    },
    searchStudents (query) {
      const q = (query || '').trim()
      if (!q) {
        this.remoteSelect.options = []
        return
      }
      this.remoteSelect.loading = true
      this.$store
        .dispatch('Admin/getAllStudentsByCriteria', { studentName: q })
        .then((response) => {
          this.remoteSelect.loading = false
          this.remoteSelect.options = response.data || []
        })
        .catch((err) => {
          console.error(err)
          this.remoteSelect.loading = false
          this.remoteSelect.options = []
        })
    },

    onRemoteStudentSelect (student) {
      if (!student || !student.userId) {
        this.clearBound()
        return
      }
      this.bindStudentAndLoad(student, { silent: false })
    },

    clearBound () {
      this.boundStudent = null
      this.assignments = []
      this.statusFilter = 'all'
      this.remoteSelect.value = null
      this.remoteSelect.options = []
      this.clearPersistedBoundStudent()
    },

    loadAssignments () {
      if (!this.boundStudent) return
      const studentId = Number(this.boundStudent.userId)
      if (!Number.isFinite(studentId) || studentId <= 0) return
      this.listLoading = true
      this.$store
        .dispatch('TyperAssignment/getStudentAssignments', { studentId })
        .then((response) => {
          const rawList = unwrapApiData(response)
          const list = Array.isArray(rawList) ? rawList : []
          this.assignments = normalizeTyperAssignmentList(list, {
            studentId: this.boundStudent.userId,
            formatToMinute: this.formatToMinute
          })
        })
        .catch((err) => {
          console.error(err)
          this.assignments = []
          const d = err.response?.data
          const msg =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '加载作业失败'
          ElMessage.error(String(msg))
        })
        .finally(() => {
          this.listLoading = false
        })
    },

    resultRecordRowClassName () {
      return 'result-record-row'
    },

    openResultSubmissionDetail (assignmentRow, resultRow) {
      if (!assignmentRow || !resultRow) return
      this.resultSubmissionAssignment = assignmentRow
      this.resultSubmissionResult = resultRow
      this.resultSubmissionVisible = true
    },

    typerSpanToInput (span) {
      if (!span) return ''
      const cls = span.className || ''
      if (cls.includes('student-typed-pending')) return ''
      return String(span.text ?? '').replace(/\u00a0/g, ' ').trim()
    },

    parseTypedContentSpans (html) {
      if (!html || typeof html !== 'string') return []
      try {
        const doc = new DOMParser().parseFromString(
          `<div id="typer-parse-root">${html}</div>`,
          'text/html'
        )
        const root = doc.getElementById('typer-parse-root')
        if (!root) return []
        return Array.from(root.querySelectorAll('span')).map((span) => ({
          className: span.getAttribute('class') || '',
          text: span.textContent ?? ''
        }))
      } catch {
        return []
      }
    },

    buildWrongWordRows (assignment, result) {
      const body = (
        (assignment.AIgeneratedText || assignment.aIgeneratedText || '').trim() ||
        (assignment.originalText || '').trim()
      )
      if (!body) return []
      const expectedWords = body.split(/\s+/).filter(Boolean)
      const caseSensitive = !!assignment.caseSensitive
      const spans = this.parseTypedContentSpans(result.typedContent)
      if (!spans.length) return []
      const n = Math.max(expectedWords.length, spans.length)
      const rows = []
      for (let i = 0; i < n; i++) {
        const correct = expectedWords[i] ?? ''
        const input = this.typerSpanToInput(spans[i])
        const ok =
          !!correct &&
          !!input &&
          normalizeTyperWord(input, caseSensitive) === normalizeTyperWord(correct, caseSensitive)
        if (ok) continue
        if (!correct && !input) continue
        rows.push({
          index: i + 1,
          correct: correct || '—',
          input: input || '（未输入）',
          punctuationOnly: isPunctuationOnlyDifference(correct, input, caseSensitive),
          tokens: buildTyperWrongWordDiffTokens(correct, input, caseSensitive)
        })
      }
      return rows
    },

    openDetail (row) {
      this.detailRow = row
      this.detailVisible = true
    },

    onRowClick (row, column) {
      if (column?.type === 'expand') return
      if (!row?.id) return
      this.$router.push({
        path: '/vocTyper',
        query: { assignmentId: row.id }
      })
    },
    resultPassTagType (passed) {
      return passed ? 'success' : 'danger'
    },

    /** mock/API 约定：pending | submitted | graded */
    statusTagType (status) {
      const map = {
        pending: 'warning',
        submitted: 'primary',
        graded: 'success'
      }
      return map[status] || 'info'
    },

    assignmentRowClassName ({ row }) {
      const s = row.status || 'pending'
      return `assignment-row assignment-row--${s}`
    }
  }
}
</script>

<style scoped lang="scss">
.teacher-typer-page {
  text-align: left;
}

.teacher-typing-assignments {
  max-width: 1100px;
  margin: 24px auto;
  padding: 0 16px;
}

.main-card {
  border-radius: 8px;
}

.card-title {
  font-size: 1.15rem;
  font-weight: 600;
}

.card-desc {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.filter-label {
  font-size: 13px;
  color: var(--el-text-color-regular);
}

.student-remote-select {
  width: 280px;
  max-width: 100%;
}

.bound-bar {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.bound-hint {
  margin: 0;
  font-size: 12px;
  flex: 1 1 100%;
  padding-left: 0;
}

.bound-label {
  font-size: 13px;
  color: var(--el-text-color-regular);
}

.hint-muted {
  margin-top: 12px;
  font-size: 13px;
  color: var(--el-text-color-placeholder);
}

.table-wrap {
  min-height: 120px;
}

.status-tag {
  font-weight: 500;
}
.assignment-results-expand {
  padding: 8px 12px;
}
.result-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

:deep(.el-table__body tr) {
  cursor: pointer;
}

/* 行背景与标签语义一致：待完成暖色、已提交蓝色调、已批改绿色调 */
:deep(.el-table .assignment-row--pending > td.el-table__cell) {
  background-color: #fdf6ec !important;
}

:deep(.el-table .assignment-row--submitted > td.el-table__cell) {
  background-color: #ecf5ff !important;
}

:deep(.el-table .assignment-row--graded > td.el-table__cell) {
  background-color: #f0f9eb !important;
}

.detail-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.5;
  max-height: 220px;
  overflow: auto;
}

.result-table-hint {
  margin: 0 0 8px;
  font-size: 12px;
}

:deep(.result-record-row) {
  cursor: pointer;
}

.result-submission-meta {
  margin-bottom: 16px;
}

.accuracy-desc {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: normal;
  color: var(--el-text-color-secondary);
}

.result-section-title {
  margin: 14px 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.result-section-pre {
  margin: 0;
  padding: 10px 12px;
  background: var(--el-fill-color-light);
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.55;
  max-height: 200px;
  overflow: auto;
}

.result-typed-html {
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  line-height: 1.65;
  word-break: break-word;
  max-height: 220px;
  overflow: auto;
  font-size: 14px;
  text-align: left;
}

.result-typed-html :deep(.student-typed-correct) {
  color: deepskyblue;
}

.result-typed-html :deep(.student-typed-wrong) {
  color: #f44336;
}

.result-typed-html :deep(.student-typed-pending) {
  color: #c8c8c8;
}

.wrong-words-table {
  width: 100%;
}

.wrong-words-table-diff {
  font-family: monospace;
  line-height: 1.55;
  word-break: break-word;
  white-space: normal;
}

.wrong-diff-char {
  display: inline-block;
  min-width: 0.62em;
  text-align: center;
}

.wrong-diff-main-char {
  display: inline;
}

.wrong-diff-correct {
  color: #333;
}

.wrong-diff-replace {
  color: #f44336;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  position: relative;
  padding-right: 0.05em;
}

.wrong-diff-extra {
  color: #ff9800;
  text-decoration: line-through;
}

.wrong-diff-expected {
  display: inline-block;
  margin-left: 0.08em;
  color: #1976d2;
  font-size: 0.78em;
  vertical-align: super;
  line-height: 1;
  text-decoration: none;
}

.wrong-diff-missing {
  color: #1976d2;
  text-decoration: none;
  background: transparent;
  border-radius: 2px;
  min-width: auto;
}

.wrong-diff-missing::before {
  content: '[';
}

.wrong-diff-missing::after {
  content: ']';
}

</style>
